import { apiClient, HttpError } from '@littlebird/auth-client';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

export enum JournalNotificationTime {
  // Legacy
  MORNING = 'morning',
  NONE = 'none',
}

export const NotificationTimeOptions = [
  { value: 'none' as const, label: 'None' },
  { value: 6 as const, label: '6 AM' },
  { value: 7 as const, label: '7 AM' },
  { value: 8 as const, label: '8 AM' },
  { value: 9 as const, label: '9 AM' },
  { value: 10 as const, label: '10 AM' },
  { value: 11 as const, label: '11 AM' },
  { value: 12 as const, label: '12 PM' },
];

export const DefaultNotificationHour = 9;

export enum JournalNotificationType {
  FULL = 'full',
  TLDR = 'tldr',
}

export enum JournalSectionType {
  POINTS = 'points',
  PARAGRAPHS = 'paragraphs',
}

export enum JournalLanguage {
  ENGLISH = 'english',
  CHINESE = 'chinese',
  DUTCH = 'dutch',
  FRENCH = 'french',
  GERMAN = 'german',
  HINDI = 'hindi',
  ITALIAN = 'italian',
  JAPANESE = 'japanese',
  KOREAN = 'korean',
  PORTUGUESE = 'portuguese',
  RUSSIAN = 'russian',
  SPANISH = 'spanish',
}

export enum JournalProgressSectionMode {
  ALWAYS = 'always',
  NEVER = 'never',
  WEEKLY = 'weekly',
}

export enum DayOfWeek {
  MONDAY = 'monday',
  TUESDAY = 'tuesday',
  WEDNESDAY = 'wednesday',
  THURSDAY = 'thursday',
  FRIDAY = 'friday',
  SATURDAY = 'saturday',
  SUNDAY = 'sunday',
}

export const ProgressSectionConfigSchema = z.object({
  mode: z.nativeEnum(JournalProgressSectionMode),
  day_of_week: z.nativeEnum(DayOfWeek).optional(),
});

export type ProgressSectionConfig = z.infer<typeof ProgressSectionConfigSchema>;

export const JournalSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  type: z.nativeEnum(JournalSectionType),
});

export type JournalSection = z.infer<typeof JournalSectionSchema>;

export const NotificationTimeSchema = z.union([
  z.literal('none'),
  z.literal(6),
  z.literal(7),
  z.literal(8),
  z.literal(9),
  z.literal(10),
  z.literal(11),
  z.literal(12),
]);

export type NotificationTime = z.infer<typeof NotificationTimeSchema>;

export const JournalConfigSchema = z.object({
  notification_time: z
    .nativeEnum(JournalNotificationTime)
    .default(JournalNotificationTime.MORNING),
  notification_hour: z.number().min(6).max(12).optional(), // New field, old users won't have this
  notification_type: z
    .nativeEnum(JournalNotificationType)
    .default(JournalNotificationType.TLDR),
  expand_sections: z.boolean().default(true),
  sections: z.array(JournalSectionSchema).default([]),
  language: z.nativeEnum(JournalLanguage).default(JournalLanguage.ENGLISH),
  progress_section: ProgressSectionConfigSchema.optional(),
  mobile_notifications_enabled: z.boolean().default(true),
});

export type JournalConfig = z.infer<typeof JournalConfigSchema>;

export const GENERATION_STATUS_QUERY_KEY = [
  'journal-generation-status',
] as const;

export const generationStatusQuery = () =>
  queryOptions({
    queryKey: GENERATION_STATUS_QUERY_KEY,
    queryFn: () =>
      apiClient.get<{ paused: boolean }>('journal/generation_status'),
  });

export const suggestedSectionsQuery = () =>
  queryOptions({
    queryKey: ['suggested-sections'],
    queryFn: async () => {
      try {
        const data = await apiClient.get<{
          suggested_sections?: unknown;
        }>('journal/suggested_sections');

        if (
          !data.suggested_sections ||
          !Array.isArray(data.suggested_sections)
        ) {
          console.info('Unexpected API response format:', data);
          return [];
        }

        console.info(
          'Suggested sections API response:',
          data.suggested_sections
        );

        const fixedSections = data.suggested_sections.map((section: any) => ({
          ...section,
          type: section.type === 'paragraph' ? 'paragraphs' : section.type,
        }));

        console.info('Fixed sections:', fixedSections);

        return z.array(JournalSectionSchema).parse(fixedSections);
      } catch (error) {
        if (error instanceof HttpError) {
          console.error(
            `Failed to fetch suggested sections: ${error.status}`,
            error
          );
        } else {
          console.error('Error processing suggested sections:', error);
        }
        return [];
      }
    },
  });

export const resetJournalSectionsMutation = {
  mutationFn: async () => {
    try {
      await apiClient.post('journal/reset_config');
      return;
    } catch (error) {
      if (error instanceof HttpError) {
        console.error(
          `Failed to reset journal sections: ${error.status}`,
          error
        );
        throw new Error(`Failed to reset journal sections: ${error.status}`);
      }
      console.error('Error resetting journal sections:', error);
      throw error;
    }
  },
};
